/**
 * Goal creation and commitment.
 *
 * The backend models each goal kind as its own mutation with its own typed
 * input (`app/graphql_endpoints/goal/*`), because the planner needs different
 * facts for a retirement corpus than for a car down-payment. The eleven the
 * planner offers are here; `config/goalTypes.js` describes the form for each
 * and builds its payload. Healthcare and Trading have creates on the backend
 * but no finalize, so they are not offered.
 *
 * COMMITTING A GOAL TAKES TWO CALLS, and this is the part that was missing.
 * `create<Type>Goal` creates the goal and its investment account and answers
 * with a PROPOSAL: `investmentplan.optimalPlan` is a simulation, computed on
 * the way out and stored nowhere. Only `finalize<Type>Plan` writes an
 * `investment_plan` row and moves the goal to `committed`.
 *
 * Skipping the second call is why every goal on this backend reads committed at
 * the goal level and draft at the plan level, with no progress rows behind it:
 * `investment_plan_progress` hangs off a plan, so a goal that never had one can
 * never report a funded status, however much money its portfolio holds.
 *
 * Finalize demands BOTH `recommendedModelId` and `recommendedVariantId` — the
 * goal's model reference portfolio is the selected variant's implementation, so
 * the backend refuses to guess which one was chosen. Variant 0 is the base
 * variant and must still be passed explicitly.
 */
import { gql } from '@apollo/client';

/**
 * The simulation a create answers with: what the plan would look like if
 * committed. Everything here is discarded unless finalize is called with it.
 *
 * `yearlyProjections` is the curve, one point per year with the three cases and
 * the contributions made by then. `forecastAssumptions` is what the projection
 * was run on, per asset class, so a reader can see the return and volatility
 * behind the numbers rather than taking them on trust.
 */
export const OPTIMAL_PLAN = `
  optimalPlan {
    startingCapital
    monthlyContributions
    investmentHorizon
    riskTolerance
    meanFinalCapital
    cumulativeContributions
    annualizedReturn
    portfolioExpectedReturn
    worst5Percentile
    best5Percentile
    probOfMeetingTarget
    meanGapToTarget
    minContributions
    sharpeRatio
    sortinoRatio
    isFeasible
    assetWeights { name weight }
    baseCase { expectedValue annualizedReturnPct cumulativeReturnPct }
    conservativeCase { expectedValue annualizedReturnPct cumulativeReturnPct }
    bestCase { expectedValue annualizedReturnPct cumulativeReturnPct }
    yearlyProjections { year baseCase conservativeCase bestCase contributionsToDate }
    forecastAssumptions { assetClass expectedReturn expectedVolatility inflationAssumption }
  }
`;

/**
 * The model the planner suggests, and the alternatives it considered.
 *
 * `implementations` carries every variant's instruments, each tagged with its
 * `variantId`, so the review step can group them and let the advisor pick the
 * variant finalize insists on.
 */
export const MODEL_CHOICE = `
  recommendedModel {
    assetClassModelId
    variantId
    modelName
    modelStandardName
    modelDescription
    riskPreference
    timeHorizonYears
    modelPortfolioId
    modelPortfolioName
    implementations { holdingSecuritySymbol securityName weight assetClassCode variantId }
  }
  suitableModels {
    id
    name
    modelStandardName
    description
    riskPreference
    timeHorizon
    category
  }
`;

const RESULT = `
  goal {
    userinvestmentgoalId
    name
    description
    status
    associatedPortfolioId
    createdAt
  }
  investmentplan {
    investmentplanId
    targetValue
    monthlyContribution
    yearlyContribution
    numberOfMonthsForPlan
    plannedTotalAssets
    plannedFundedStatus
    targetInvestmentRisk
    status
    description { status summary contributions investmentGains actionItems progress recommendations }
    aiSummary { resultFlag summary recommendations }
    ${OPTIMAL_PLAN}
    ${MODEL_CHOICE}
  }
`;

export const CREATE_EDUCATION_GOAL = gql`
  mutation CreateEducationGoal($input: EducationCreate!) {
    createEducationGoal(input: $input) {
      educationId
      startDate
      currentAge
      totalExpenses
      numberOfMonths
      monthlyContribution
      educationprogram { program numberOfMonths approximateCost }
      ${RESULT}
    }
  }
`;

export const CREATE_RETIREMENT_GOAL = gql`
  mutation CreateRetirementGoal($input: RetirementCreate!) {
    createRetirmentGoal(input: $input) {
      retirementId
      plannedRetirementAge
      monthlyContribution
      amountRequiredInRetirement
      incomeRequiredInRetirement
      inflationAssumptionUsed
      ${RESULT}
    }
  }
`;

export const CREATE_TRAVEL_GOAL = gql`
  mutation CreateTravelGoal($input: TravelCreate!) {
    createTravelGoal(input: $input) {
      travelId
      expectedBudget
      monthsToTravel
      monthlyContribution
      travelType
      ${RESULT}
    }
  }
`;

export const CREATE_BUY_HOME_GOAL = gql`
  mutation CreateBuyHomeGoal($input: BuyHomeCreate!) {
    createBuyhomeGoal(input: $input) {
      buyhomeId
      propertyType
      buyhomePurpose
      expectedPurchasePrice
      expectedDownpaymentPercent
      targetCapitalForDownpayment
      timeToPlannedPurchaseInMonths
      monthlyContribution
      ${RESULT}
    }
  }
`;

export const CREATE_BUY_VEHICLE_GOAL = gql`
  mutation CreateBuyVehicleGoal($input: BuyVehicleCreate!) {
    createBuyvehicleGoal(input: $input) {
      buyvehicleId
      expectedPurchasePrice
      expectedDownpaymentPercent
      targetCapitalForDownpayment
      timeToPlannedPurchaseInMonths
      monthlyContribution
      ${RESULT}
    }
  }
`;

export const CREATE_EMERGENCY_FUND_GOAL = gql`
  mutation CreateEmergencyFundGoal($input: EmergencyFundCreate!) {
    createEmergencyFundGoal(input: $input) {
      goalType
      monthlyExpenses
      monthsCoverage
      currentFund
      targetAmount
      monthlyContribution
      ${RESULT}
    }
  }
`;

export const CREATE_KIDS_MARRIAGE_GOAL = gql`
  mutation CreateKidsMarriageGoal($input: KidsMarriageCreate!) {
    createKidsMarriageGoal(input: $input) {
      goalType
      beneficiaryName
      targetAmount
      targetYear
      yearsToEvent
      currentSavings
      monthlyContribution
      ${RESULT}
    }
  }
`;

export const CREATE_DEBT_PAYDOWN_GOAL = gql`
  mutation CreateDebtPaydownGoal($input: DebtPaydownCreate!) {
    createDebtPaydownGoal(input: $input) {
      goalType
      debtName
      loanType
      outstandingAmount
      interestRate
      currentEmi
      targetPayoffMonths
      strategy
      recommendedStrategy
      monthsToClose
      totalInterestSaved
      originalMonthsRemaining
      ${RESULT}
    }
  }
`;

export const CREATE_CAPITAL_TARGET_GOAL = gql`
  mutation CreateCapitalTargetGoal($input: CapitalTargetCreate!) {
    createCapitalTargetGoal(input: $input) {
      goalType
      targetAmount
      monthsToTarget
      currentSavings
      monthlyContribution
      purpose
      trackingError
      excessReturnTarget
      ${RESULT}
    }
  }
`;

export const CREATE_LONG_TERM_SAVINGS_GOAL = gql`
  mutation CreateLongTermSavingsGoal($input: LongTermSavingsCreate!) {
    createLongTermSavingsGoal(input: $input) {
      goalType
      monthlyContribution
      currentSavings
      targetAmount
      monthsToTarget
      ${RESULT}
    }
  }
`;

export const CREATE_GOLD_SAVINGS_GOAL = gql`
  mutation CreateGoldSavingsGoal($input: GoldSavingsCreate!) {
    createGoldSavingsGoal(input: $input) {
      goalType
      measurement
      purpose
      goldSymbol
      monthlyContributionInr
      targetGrams
      targetAmountInr
      projectedGrams
      goldPricePerGram
      monthsToTarget
      ${RESULT}
    }
  }
`;

/* ── committing the plan ────────────────────────────────────── */

/**
 * The second call, one per goal type.
 *
 * Every one of them takes the same `FinalizeGoalPlanInput` and answers with the
 * same four fields, so the shape is written once. What differs is only which
 * goal handler runs, which is why the backend spells out eleven mutations
 * rather than taking the type as an argument.
 *
 * The input is the planner's own output handed straight back: the probability,
 * the mean gap, the three scenario cases and the asset split all come off the
 * `plan<Type>` subscription's `optimalPlan`. Only the model and variant are a
 * decision, and both are required.
 *
 * ELEVEN, NOT THIRTEEN. Healthcare and Trading have create and update
 * mutations but no finalize, so a goal of either kind cannot be committed at
 * all, and neither is offered.
 */
/**
 * Written out one by one rather than built by a helper. `scripts/validate-graphql.js`
 * reads these documents statically and expands only `${CONST}`, so a document
 * assembled from a function argument is a syntax error to the one check that
 * proves these still match the live schema.
 */
const FINALIZED = `
  investmentplanId
  userinvestmentgoalId
  status
  message
`;

export const FINALIZE_EDUCATION_PLAN = gql`
  mutation FinalizeEducationPlan($input: FinalizeGoalPlanInput!) {
    finalizeEducationPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_RETIREMENT_PLAN = gql`
  mutation FinalizeRetirementPlan($input: FinalizeGoalPlanInput!) {
    finalizeRetirementPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_TRAVEL_PLAN = gql`
  mutation FinalizeTravelPlan($input: FinalizeGoalPlanInput!) {
    finalizeTravelPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_BUY_HOME_PLAN = gql`
  mutation FinalizeBuyhomePlan($input: FinalizeGoalPlanInput!) {
    finalizeBuyhomePlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_BUY_VEHICLE_PLAN = gql`
  mutation FinalizeBuyvehiclePlan($input: FinalizeGoalPlanInput!) {
    finalizeBuyvehiclePlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_EMERGENCY_FUND_PLAN = gql`
  mutation FinalizeEmergencyFundPlan($input: FinalizeGoalPlanInput!) {
    finalizeEmergencyFundPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_KIDS_MARRIAGE_PLAN = gql`
  mutation FinalizeKidsMarriagePlan($input: FinalizeGoalPlanInput!) {
    finalizeKidsMarriagePlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_DEBT_PAYDOWN_PLAN = gql`
  mutation FinalizeDebtPaydownPlan($input: FinalizeGoalPlanInput!) {
    finalizeDebtPaydownPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_CAPITAL_TARGET_PLAN = gql`
  mutation FinalizeCapitalTargetPlan($input: FinalizeGoalPlanInput!) {
    finalizeCapitalTargetPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_LONG_TERM_SAVINGS_PLAN = gql`
  mutation FinalizeLongTermSavingsPlan($input: FinalizeGoalPlanInput!) {
    finalizeLongTermSavingsPlan(input: $input) { ${FINALIZED} }
  }
`;

export const FINALIZE_GOLD_SAVINGS_PLAN = gql`
  mutation FinalizeGoldSavingsPlan($input: FinalizeGoalPlanInput!) {
    finalizeGoldSavingsPlan(input: $input) { ${FINALIZED} }
  }
`;

/**
 * Turn the analysis plus the chosen model into the finalize input.
 *
 * Kept beside the documents rather than in the modal because the mapping is the
 * contract between the two calls, not a detail of one screen: the planner's
 * `optimalPlan` uses one set of names and `FinalizeGoalPlanInput` another, and
 * a field dropped here is a field the committed plan is missing forever.
 *
 * The goal's own figures — target, horizon, savings and contribution — come
 * from the goal (`config/goalTypes.js` → `commit`), not the plan: the planner's
 * response carries `targetValue` and `numberOfMonthsForPlan` as null, and what
 * the advisor commits to is the contribution they entered, not one the planner
 * solved for. A horizon the goal does not state (an emergency fund) falls back
 * to the planner's own, which it reports in YEARS.
 *
 * Every figure below is non-null on the input. Where the planner left one out,
 * the same neutral value the app sends stands in — a zero gap or probability,
 * a feasible verdict — rather than failing the commit on schema validation.
 */
export function finalizeInput({ goalId, plan, model, variantId, figures = {} }) {
  const o = plan?.optimalPlan ?? {};
  const ai = plan?.aiSummary ?? {};
  const scenario = (s) => ({
    expectedValue: s?.expectedValue ?? 0,
    annualizedReturnPct: s?.annualizedReturnPct ?? 0,
    cumulativeReturnPct: s?.cumulativeReturnPct ?? 0,
  });
  const horizon = o.investmentHorizon == null ? null : Math.round(Number(o.investmentHorizon) * 12);

  return {
    userinvestmentgoalId: goalId,
    monthlyContribution: figures.monthlyContribution ?? o.monthlyContributions ?? 0,
    currentSavings: figures.currentSavings ?? o.startingCapital ?? 0,
    targetAmount: figures.targetAmount ?? plan?.targetValue ?? null,
    monthsToTarget: figures.monthsToTarget ?? plan?.numberOfMonthsForPlan ?? horizon ?? null,
    riskTolerance: o.riskTolerance ?? figures.riskTolerance ?? plan?.targetInvestmentRisk ?? null,
    probOfMeetingTarget: o.probOfMeetingTarget ?? 0,
    meanGapToTarget: o.meanGapToTarget ?? 0,
    isFeasible: o.isFeasible ?? true,
    baseCase: scenario(o.baseCase),
    conservativeCase: scenario(o.conservativeCase),
    bestCase: scenario(o.bestCase),
    assetAllocation: (o.assetWeights ?? []).map((a) => ({ name: a.name, weight: a.weight })),
    recommendedModelName: model?.modelStandardName ?? model?.modelName ?? null,
    recommendedModelRisk: model?.riskPreference ?? null,
    recommendedModelId: model?.assetClassModelId ?? null,
    /* Never `|| 0`: the base variant IS 0, and a falsy check would rewrite a
       deliberate choice of variant 0 as "unset" and back again to 0 anyway,
       hiding a missing selection instead of failing on it. */
    recommendedVariantId: variantId ?? null,
    aiResultFlag: ai.resultFlag ?? null,
    aiSummary: ai.summary ?? null,
    aiRecommendations: ai.recommendations ?? null,
  };
}
